import find from 'lodash/find';
import get from 'lodash/get';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import { useEffect, useState } from 'react';

import BusinessHours from 'src/models/BusinessHours/BusinessHours';
import agentsResource from 'src/actions/agent/agentsResource';
import businessHoursResource from 'src/actions/businessHours/businessHoursResource';
import useResource, {
  STATUS_LOADING,
  STATUS_SUCCEEDED,
  getConsolidatedState,
} from 'src/presentation/hooks/useResource';
import { DAY_OF_WEEK } from 'src/presentation/components/pages/shared/TimeConstants';
import { toDateTimeString } from 'src/presentation/components/pages/shared/utils/TimeUtils';

interface BusinessHoursForTable extends BusinessHours {
  updatedAtString: string;
  updatedByString: string;
}

const useFetchBusinessHours = () => {
  const [sortedBusinessHours, setSortedBusinessHours] = useState<BusinessHoursForTable[]>();
  const [loadingState, setLoadingState] = useState(STATUS_LOADING);
  const {
    values: businessHours,
    errors: businessHoursErrors,
    loadingState: businessHoursLoadingState,
  } = useResource(businessHoursResource);
  const { values: agents, errors: agentsErrors, loadingState: agentsLoadingState } = useResource(agentsResource);
  const { resetErrors } = businessHoursResource.actions;

  useEffect(() => {
    const loadingState = getConsolidatedState([businessHoursLoadingState, agentsLoadingState]);
    setLoadingState(loadingState);
    if (loadingState !== STATUS_SUCCEEDED) {
      return;
    }
    const sorted = sortBy(
      map(businessHours, businessHour => {
        DAY_OF_WEEK.forEach(day => {
          businessHour.officeHours[day] = businessHour.officeHours[day] ?? [];
        });
        businessHour.officeHours.exceptions = businessHour.officeHours.exceptions ?? [];

        const updatedAtString = toDateTimeString(businessHour.updatedAt);
        const updatedById = get(businessHour, 'updatedBy.userId');
        const createdByAgent = find(agents, agent => agent.id === updatedById);
        const agentName = get(createdByAgent, 'name');
        return { ...businessHour, updatedAtString, updatedByString: agentName };
      }),
      item => item.name.toLowerCase()
    );
    setSortedBusinessHours(sorted);
  }, [agents, agentsLoadingState, businessHours, businessHoursLoadingState, setLoadingState, setSortedBusinessHours]);

  const errors = businessHoursErrors || agentsErrors;

  return { businessHours: sortedBusinessHours, errors, loadingState, resetErrors };
};

export default useFetchBusinessHours;
